// Fills <delivery-estimate> with an arrival range in the warehouse's time zone.
// Orders placed on a business day before the cutoff ship that day; later orders ship the next
// business day. Arrival is the ship day plus the element's min-max business days in transit.
// Rendered by snippets/delivery-estimate.liquid, which holds the settings and the copy.

const WAREHOUSE_TIME_ZONE = 'America/New_York';
const DAY_MS = 24 * 60 * 60 * 1000;

const warehouseParts = new Intl.DateTimeFormat('en-US', {
  timeZone: WAREHOUSE_TIME_ZONE,
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  hourCycle: 'h23',
});
const monthName = new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' });

/** Today's calendar date in the warehouse, as a UTC-midnight Date, plus the warehouse hour. */
function warehouseNow() {
  const parts = Object.fromEntries(warehouseParts.formatToParts(new Date()).map((p) => [p.type, p.value]));
  return {
    date: new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day))),
    hour: Number(parts.hour),
  };
}

/** @param {Date} date @param {Set<string>} holidays */
function isBusinessDay(date, holidays) {
  const weekday = date.getUTCDay();
  return weekday !== 0 && weekday !== 6 && !holidays.has(date.toISOString().slice(0, 10));
}

/** @param {Date} date @param {number} days @param {Set<string>} holidays */
function addBusinessDays(date, days, holidays) {
  let next = date;
  while (days > 0) {
    next = new Date(next.getTime() + DAY_MS);
    if (isBusinessDay(next, holidays)) days--;
  }
  return next;
}

/** "Oct 16–21", or "Oct 30–Nov 4" across a month. */
function formatRange(from, to) {
  const fromMonth = monthName.format(from);
  const toMonth = monthName.format(to);
  const end = fromMonth === toMonth ? `${to.getUTCDate()}` : `${toMonth} ${to.getUTCDate()}`;
  return `${fromMonth} ${from.getUTCDate()}–${end}`;
}

class DeliveryEstimate extends HTMLElement {
  connectedCallback() {
    const min = Number(this.dataset.minDays);
    const max = Number(this.dataset.maxDays);
    const cutoffHour = Number(this.dataset.cutoffHour);
    const output = this.querySelector('[data-delivery-estimate-dates]');
    if (!output || !(min > 0) || !(max >= min)) return;

    const holidays = new Set((this.dataset.holidays || '').match(/\d{4}-\d{2}-\d{2}/g) || []);
    const now = warehouseNow();
    const shipsToday = isBusinessDay(now.date, holidays) && now.hour < cutoffHour;
    const shipDate = shipsToday ? now.date : addBusinessDays(now.date, 1, holidays);

    output.textContent = formatRange(
      addBusinessDays(shipDate, min, holidays),
      addBusinessDays(shipDate, max, holidays)
    );
    this.toggleAttribute('data-ready', true);
  }
}

if (!customElements.get('delivery-estimate')) customElements.define('delivery-estimate', DeliveryEstimate);
